import { ILabelSettings } from "@amcharts/amcharts5";
import { forIn, merge } from "lodash";
import { v4 as uuid } from "uuid";

import { PLAN_DUAL_MODULE, PLAN_EXPLORE_MODULE, PLAN_MODULE } from "@/constants";
import {
  VISUAL_DATE_FORMAT,
  VISUAL_MONTH_FORMAT,
  VISUAL_QUARTER_FORMAT,
  VISUAL_WEEK_FORMAT,
} from "@/helpers/formatters";
import { chartsSettings, getChartAllControls } from "@/settings/charts";
import inputsDictionary from "@/settings/inputsDictionary";
import planInputsDictionary from "@/settings/planInputsDictionary";
import { WIDGET_TAB_SPECIAL, widgetTabs } from "@/settings/widgetFamilies";
import { Config } from "@/types/charts";
import { InputDictionary } from "@/types/common";

export interface AvailableWidget {
  id: string;
  widget: string;
  title: string;
  chartType: string;
  chartName: string;
  defaultHeight: number;
  draft?: string[];
  exploreOnly?: boolean;
  planOnly?: boolean;
  settings: Record<string, unknown>;
  planSettings: Record<string, unknown>;
  controls: Record<string, unknown>[];
  family: string;
}

export interface AvailableTab {
  id?: string;
  title: string;
  family: string;
  list: AvailableWidget[];
}

export interface FamilyTab {
  title: string;
  family: string;
  order: number;
  list: AvailableWidget[];
}

export type Mode = "plan" | "explore" | "all";
const toBool = (v: string | number | boolean | string[] | null) => v === true || v === "true";
const isExcludedFamily = (familyName: string, excluded: Set<string>) => excluded.has(familyName);

export const WIDGETS_TYPES = {
  SUMMARY: "SummaryWidget",
  CHART: "ChartWidget",
  TABLE: "TableWidget",
  PLAN_SUMMARY: "PlanSummaryWidget",
  TRADITIONAL_CHANNEL_TABLE: "TraditionalChannelTableWidget",
};

const getWidgetType = (chartName: string) => {
  switch (chartName) {
    case "summary":
      return WIDGETS_TYPES.SUMMARY;
    case "dataTable":
    case "budgetAllocationTable":
    case "recommendationsTable":
      return WIDGETS_TYPES.TABLE;
    case "planSummaryTable":
      return WIDGETS_TYPES.PLAN_SUMMARY;
    case "traditionalTvGrpScoreTable":
    case "traditionalTvViabilityTable":
    case "traditionalRadioGrpScoreTable":
    case "traditionalRadioViabilityTable":
      return WIDGETS_TYPES.TRADITIONAL_CHANNEL_TABLE;
    default:
      return WIDGETS_TYPES.CHART;
  }
};

const PLAN_EXPLORE_EXPLORE_DICTIONARIES = new Set([
  "dimensionMetrics",
  "displayMetrics",
  "nullableDisplayMetrics",
  "timeMetrics",
  "summaryMetrics",
  "mapGeographyTypes",
  "advertisersMetrics",
]);

export const getAxisTitle = (
  settings: Record<string, Record<string, string | number | boolean>>,
  axisParam: string,
  value?: string,
  module?: string,
  params?: Record<string, unknown>
): string => {
  const axisSetting = settings[axisParam];
  const dictionaryName = axisSetting?.dictionary;
  if (typeof dictionaryName !== "string") return "";

  let dictionarySource: Record<string, unknown> = inputsDictionary;
  if (module === PLAN_DUAL_MODULE) {
    const sourceFrom = axisSetting?.sourceFrom as string | undefined;
    let isExploreSource = false;
    if (sourceFrom) {
      const currentSourceVal =
        (params?.[sourceFrom] as string | undefined) ?? (settings[sourceFrom]?.default as string);
      isExploreSource = currentSourceVal === "explore";
    }
    dictionarySource = isExploreSource ? inputsDictionary : planInputsDictionary;
  } else if (module === PLAN_EXPLORE_MODULE) {
    dictionarySource = PLAN_EXPLORE_EXPLORE_DICTIONARIES.has(dictionaryName) ? inputsDictionary : planInputsDictionary;
  } else if (module === PLAN_MODULE) {
    dictionarySource = planInputsDictionary;
  }

  const dictionaryList = dictionarySource[dictionaryName];

  if (!Array.isArray(dictionaryList)) return "";

  const found = (dictionaryList as InputDictionary[]).find(
    (el) => el.value === (value !== undefined ? value : settings[axisParam].default)
  );

  return found?.title || "";
};

const getPreparedSettings = (settings: Record<string, Record<string, string | number | boolean>>, module?: string) => {
  const preparedSettings: Record<string, unknown> = {};

  forIn(settings, (setting, key) => {
    const defaultValue = setting?.forAxis
      ? getAxisTitle(settings, setting.forAxis as string, undefined, module)
      : setting?.default;

    preparedSettings[key] = setting?.value === undefined ? defaultValue : setting?.value;
  });

  return preparedSettings;
};

export const getChartSettings = (chartName: string, familyName: string, module?: string) => {
  const settingsByFamily = chartsSettings[chartName]?.familySettings?.[familyName];

  if (settingsByFamily) {
    const settings = merge({}, chartsSettings[chartName].settings, settingsByFamily);
    return getPreparedSettings(settings, module);
  }

  return getPreparedSettings(chartsSettings[chartName].settings, module);
};

export const getChartPlanSettings = (chartName: string, familyName: string, module?: string) => {
  const settingsByFamily = chartsSettings[chartName]?.familySettings?.[familyName];

  if (settingsByFamily) {
    const planSettings = merge({}, chartsSettings[chartName].planSettings, settingsByFamily);
    return getPreparedSettings(planSettings, module);
  }

  return getPreparedSettings(chartsSettings[chartName].planSettings, module);
};

export const getChartPlanExploreSettings = (chartName: string, familyName: string, module?: string) => {
  const settingsByFamily = chartsSettings[chartName]?.familySettings?.[familyName];
  const exploreSettings = chartsSettings[chartName].planExploreSettings ?? chartsSettings[chartName].planSettings;

  if (settingsByFamily) {
    const planExploreSettings = merge({}, exploreSettings, settingsByFamily);
    return getPreparedSettings(planExploreSettings, module);
  }

  return getPreparedSettings(exploreSettings, module);
};

export const getChartPlanDualSettings = (chartName: string, familyName: string, module?: string) => {
  const settingsByFamily = chartsSettings[chartName]?.familySettings?.[familyName];
  const dualSettings =
    chartsSettings[chartName].planDualSettings ??
    chartsSettings[chartName].planExploreSettings ??
    chartsSettings[chartName].planSettings;

  if (settingsByFamily) {
    const planDualSettings = merge({}, dualSettings, settingsByFamily);
    return getPreparedSettings(planDualSettings, module);
  }

  return getPreparedSettings(dualSettings, module);
};

const getChartSettingGroup = (
  chartName: string,
  groupName?: string,
  familyName?: string
): string | number | string[] | boolean | null => {
  if (!familyName || chartsSettings[chartName][groupName as keyof Config][familyName] === undefined) {
    return chartsSettings[chartName][groupName as keyof Config];
  }

  return chartsSettings[chartName][groupName as keyof Config][familyName];
};

const getControlValues = (controls: Record<string, string | number | boolean | object>[]) => {
  return controls.map((control) => ({
    [control.name as string]: control.value !== undefined ? control.value : control.default,
  }));
};

const getChartControlValues = (chartName: string, familyName: string) => {
  const controls = getChartAllControls(chartName, familyName);

  return controls ? getControlValues(controls) : [];
};

export const getChartsWidgets = (
  charts: string[],
  familyName: string,
  mode: Mode = "all",
  excludedFamilies: string[] = []
): AvailableWidget[] => {
  const excluded = new Set(excludedFamilies);
  if (isExcludedFamily(familyName, excluded)) return [];

  const availableCharts: AvailableWidget[] = [];

  charts.forEach((chartName) => {
    const exploreOnly = toBool(getChartSettingGroup(chartName, "exploreOnly"));
    const planOnly = toBool(getChartSettingGroup(chartName, "planOnly"));

    if (mode === "plan" && exploreOnly) return;
    if (mode === "explore" && planOnly) return;

    availableCharts.push({
      id: uuid(),
      widget: getWidgetType(chartName),
      chartType: chartName,
      chartName: getChartSettingGroup(chartName, "title") as string,
      exploreOnly,
      planOnly,
      title: getChartSettingGroup(chartName, "title") as string,
      defaultHeight: (getChartSettingGroup(chartName, "defaultHeight") as number) || 5,
      draft: getChartSettingGroup(chartName, "draft") as string[],
      family: familyName,
      settings: getChartSettings(chartName, familyName, mode),
      planSettings: getChartPlanSettings(chartName, familyName, mode),
      controls: getChartControlValues(chartName, familyName),
    });
  });

  return availableCharts.sort((a, b) => (a.title > b.title ? 1 : -1));
};

const getAllCharts = (isRecommended: boolean, mode: Mode, excludedFamilies: string[] = []): AvailableWidget[] => {
  const chartsByTab: AvailableWidget[] = [];
  const allSettings = Object.entries(chartsSettings);
  const excluded = new Set(excludedFamilies);

  allSettings.forEach(([chartName, setting]) => {
    const families = isRecommended ? setting.recommendedFamily : setting.family;
    if (families?.length) {
      forIn(families, (family) => {
        const fam = family as string;
        if (!isExcludedFamily(fam, excluded)) {
          chartsByTab.push(...getChartsWidgets([chartName], fam, mode, excludedFamilies));
        }
      });
    }
  });

  return chartsByTab
    .sort((a, b) => (widgetTabs[a.family].title > widgetTabs[b.family].title ? 1 : -1))
    .sort((a, b) => (b.title <= a.title ? 1 : -1));
};

const getChartsByFamily = (familyName: string, excludedFamilies: string[] = []): string[] => {
  const excluded = new Set(excludedFamilies);
  if (isExcludedFamily(familyName, excluded)) return [];

  const charts: string[] = [];
  const allSettings = Object.entries(chartsSettings);

  allSettings.forEach((el) => {
    if (el[1].family.includes(familyName)) {
      charts.push(el[0]);
    }
  });

  return charts;
};

const createFamilyTabs = (mode: Mode = "all", excludedFamilies: string[] = []): FamilyTab[] => {
  const tabs: FamilyTab[] = [];
  const excluded = new Set(excludedFamilies);

  forIn(widgetTabs, (family) => {
    const { title, name, order } = family;
    const isRecommended = name === WIDGET_TAB_SPECIAL.RECOMMENDED;
    const isAll = name === WIDGET_TAB_SPECIAL.ALL;
    const isSpecial = isRecommended || isAll;

    if (!isSpecial && isExcludedFamily(name, excluded)) {
      return;
    }

    tabs.push({
      title,
      family: name,
      order,
      list: isSpecial
        ? getAllCharts(isRecommended, mode, excludedFamilies)
        : getChartsWidgets(getChartsByFamily(name, excludedFamilies), name, mode, excludedFamilies),
    });
  });

  return tabs.sort((a, b) => a.order - b.order);
};

export const familyTabs = createFamilyTabs();
export const getFamilyTabs = (mode: Mode = "all", excludedFamilies: string[] = []): FamilyTab[] =>
  createFamilyTabs(mode, excludedFamilies);

export const getTimeAxisName = (currentSettings: Record<string, unknown>): string => {
  return (
    Object.keys(currentSettings).find((key) =>
      inputsDictionary.timeMetrics.find((timeMetric) => timeMetric.value === currentSettings[key])
    ) || ""
  );
};

export const chartFontSettings: Record<string, ILabelSettings> = {
  label: {
    fontFamily: "Roboto",
  },
  legend: {
    fontFamily: "Roboto",
  },
  axisTitle: {
    fontSize: 20,
    fontWeight: "500",
    fontFamily: "Roboto",
  },
};

export const chartInitialSettings = {
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
};

export const dateAxisDateFormats = {
  day: VISUAL_DATE_FORMAT,
  week: VISUAL_WEEK_FORMAT,
  month: VISUAL_MONTH_FORMAT,
  quarter: VISUAL_QUARTER_FORMAT,
};

export const getAxisDateFormat = (timeUnit: string) => {
  switch (timeUnit) {
    case "quarter":
      return { month: VISUAL_QUARTER_FORMAT };
    default:
      return dateAxisDateFormats;
  }
};
